import { FlowType } from '@cash-web/protos/squareup/cash/plasma/flows/FlowProto.pb'
import { ClientScenario } from '@cash-web/protos/squareup/franklin/api/client_scenario.pb'
import { useQueryGetProfileInfo } from '@cash-web/shared-data-access-api/react-query'
import { useQueryGetMoneyPoolPublicPreview } from '@cash-web/shared-data-access-api/react-query/useQueryGetMoneyPoolPublicPreview'
import { useQueryGetProfile } from '@cash-web/shared-data-access-api/react-query/useQueryGetProfile'
import { useQueryGetSavingsOverview } from '@cash-web/shared-data-access-api/react-query/useQueryGetSavingsOverview'
import { useQueryGetSettingsPageDetails } from '@cash-web/shared-data-access-api/react-query/useQueryGetSettingsPageDetails'
import { FlowStartMethods } from '@cash-web/shared-flow-data-access-context'

type BaseFlowConfiguration = Omit<Parameters<FlowStartMethods['startWithFlow']>[0], 'flow'>

type GetFlowConfiguration = {
  [K in FlowType]: BaseFlowConfiguration & {
    flow?: {
      initiationData: string
    }
  }
}
const activitiesRootQueryKey = 'account-activities'
const activityByTransactionIdQueryKey = 'useQueryUseQueryGetActivityByTransactionId'

/* eslint sort-keys: "warn" */
export const getFlowConfiguration: Partial<GetFlowConfiguration> = {
  APPROVE_SPONSORSHIP: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [activitiesRootQueryKey],
    trackingFlowName: 'approve-sponsorship',
  },
  AUTHENTICATE_TRANSACTIONS: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [activitiesRootQueryKey],
    trackingFlowName: 'authenticate-transactions',
  },
  CANCEL_P2P_FIAT_PAYMENT: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [useQueryGetProfile.rootQueryKey, activitiesRootQueryKey],
    trackingFlowName: 'cancel-p2p-fiat-payment',
  },
  CHANGE_CARD_DESIGN: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [useQueryGetProfile.rootQueryKey, useQueryGetProfileInfo.rootQueryKey],
    trackingFlowName: 'change-card-design',
  },
  CONVERT_STANDARD_CASH_OUT_TO_INSTANT: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [useQueryGetProfile.rootQueryKey, activitiesRootQueryKey],
    trackingFlowName: 'convert-standard-cash-out-to-instant',
  },
  DISABLE_ISSUED_CARD_IN_POSTCARD: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [useQueryGetProfile.rootQueryKey],
    trackingFlowName: 'Virtual Card: Disable', // keeping this for now to match the old flow name
  },
  ENABLE_ISSUED_CARD: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [useQueryGetProfile.rootQueryKey],
    trackingFlowName: 'Virtual Card: Enable', // keeping this for now to match the old flow name
  },
  ENABLE_SECURITY_LOCK_FOR_MOVE_MONEY: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [useQueryGetProfile.rootQueryKey],
    trackingFlowName: 'enable-security-lock-for-money-move',
  },
  OMS_CHANGE_LOAN_AUTOPAY_FLOW: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [],
    trackingFlowName: 'oms-change-loan-autopay-flow',
  },
  OMS_INITIATE_LOAN_PAYMENT_FLOW: {
    clientScenario: ClientScenario.PLASMA,
    // TODO: Invalidate afterpay applet queries when/if available
    queriesToInvalidate: [
      activitiesRootQueryKey,
      activityByTransactionIdQueryKey,
      useQueryGetProfile.rootQueryKey,
      useQueryGetProfileInfo.rootQueryKey,
    ],
    trackingFlowName: 'oms-initiate-loan-payment-flow',
  },
  OMS_VIEW_DOCUMENT_SELECTION_FLOW: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [],
    trackingFlowName: 'oms-view-document-selection-flow',
  },
  PIGGYBANK_SAVINGS_ROUND_UP: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [useQueryGetProfile.rootQueryKey, useQueryGetSavingsOverview.rootQueryKey],
    trackingFlowName: 'piggybank-savings-round-up',
  },
  POSTBANK_RETRIEVE_BANKING_INFORMATION: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [],
    trackingFlowName: 'postbank-retrieve-banking-information',
  },
  REFUND_P2P_FIAT_PAYMENT: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [useQueryGetProfile.rootQueryKey, activitiesRootQueryKey],
    trackingFlowName: 'refund-p2p-fiat-payment',
  },
  REMOVE_SAVINGS_GOAL_V2: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [useQueryGetProfile.rootQueryKey, useQueryGetSavingsOverview.rootQueryKey],
    trackingFlowName: 'remove-savings-goal-v2',
  },
  REQUEST_PHYSICAL_CARD: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [useQueryGetProfile.rootQueryKey, useQueryGetProfileInfo.rootQueryKey],
    trackingFlowName: 'request-physical-card',
  },
  RESUME_P2P_FIAT_PAYMENT: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [useQueryGetProfile.rootQueryKey, activitiesRootQueryKey],
    trackingFlowName: 'resume-p2p-fiat-payment',
  },
  REVIEW_EXISTING_DISPUTE_REQUEST: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [],
    trackingFlowName: 'review-existing-dispute-request',
  },
  REVIEW_LOGIN: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [activitiesRootQueryKey],
    trackingFlowName: 'review-login',
  },
  SAVINGS_NUX: {
    queriesToInvalidate: [useQueryGetSavingsOverview.rootQueryKey],
    // To ensure a smooth transition to the /savings page at end of the NUX flow, the get-savings-overview cache must be cleared.
    // This guarantees that the customer’s onboarding status is up-to-date, preventing them from being redirected due to outdated cache data.
    queriesToRemove: [useQueryGetSavingsOverview.rootQueryKey],
    trackingFlowName: 'savings-nux',
  },
  SEND_OON_POOL_CONTRIBUTION_PAYMENT: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [useQueryGetMoneyPoolPublicPreview.rootQueryKey],
    trackingFlowName: 'send-oon-pool-contribution-payment',
  },
  SET_OR_UPDATE_PASSCODE: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [useQueryGetProfile.rootQueryKey],
    trackingFlowName: 'passcode-set-or-update',
  },
  SET_OR_UPDATE_PASSWORD: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [useQueryGetProfile.rootQueryKey, useQueryGetSettingsPageDetails.rootQueryKey],
    trackingFlowName: 'taxes-password-set-or-update',
  },
  SET_SAVINGS_GOAL_V2: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [useQueryGetProfile.rootQueryKey, useQueryGetSavingsOverview.rootQueryKey],
    trackingFlowName: 'set-savings-goal-v2',
  },
  TOTP: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [useQueryGetSettingsPageDetails.rootQueryKey, useQueryGetProfile.rootQueryKey],
    trackingFlowName: 'taxes-authenticator-totp',
  },
  UPDATE_SAVINGS_GOAL_V2: {
    clientScenario: ClientScenario.PLASMA,
    queriesToInvalidate: [useQueryGetProfile.rootQueryKey, useQueryGetSavingsOverview.rootQueryKey],
    trackingFlowName: 'update-savings-goal-v2',
  },
}
/* eslint-disable sort-keys */
